import { DefaultBodyType, HttpResponse, PathParams, http } from 'msw';

export function storageMock() {
  const storage: any = {};

  return {
    setItem: function (key, value) {
      storage[key] = value || '';
    },
    getItem: function (key) {
      return key in storage ? storage[key] : null;
    },
    removeItem: function (key) {
      delete storage[key];
    },
    get length() {
      return Object.keys(storage).length;
    },
    key: function (i) {
      const keys = Object.keys(storage);
      return keys[i] || null;
    },
  } as Storage;
}

import { CuratedReportsDataSource } from '@Shared/models/CuratedReports/CuratedReportsDataSource';
import type { CuratedReport } from '@Shared/models/CuratedReports/CuratedReports';

// Mock data for curated reports API
export const mockCuratedReportSlideData: {
  count: number;
  data: {
    rollupData: any;
    dimensionMetadata: Record<string, { friendlyName: string }>;
    measureMetadata: Record<string, { friendlyName: string; format: string }>;
    highlights?: any[];
  };
} = {
  count: 1,
  data: {
    rollupData: {
      value: '',
      totalMeasures: {
        npsScore: { CurrentValue: 0.08333333333333333 },
        recordCount: { CurrentValue: 12.0 },
        internalRecordCount: { CurrentValue: 12.0 },
        salesAmount: { CurrentValue: 150000 },
        revenueAmount: { CurrentValue: 120000 },
      },
      columnDimension: {
        key: 'State',
        values: [
          {
            value: 'S-1',
            totalMeasures: {
              npsScore: { CurrentValue: -0.2857142857142857 },
              recordCount: { CurrentValue: 7.0 },
              internalRecordCount: { CurrentValue: 7.0 },
            },
            columnDimension: {
              key: 'Centre',
              values: [
                {
                  value: 'A',
                  totalMeasures: {
                    npsScore: { CurrentValue: -0.8 },
                    recordCount: { CurrentValue: 5.0 },
                    internalRecordCount: { CurrentValue: 5.0 },
                  },
                },
                {
                  value: 'B',
                  totalMeasures: {
                    npsScore: { CurrentValue: 1.0 },
                    recordCount: { CurrentValue: 2.0 },
                    internalRecordCount: { CurrentValue: 2.0 },
                  },
                },
              ],
            },
          },
          {
            value: 'S-2',
            totalMeasures: {
              npsScore: { CurrentValue: 0.6 },
              recordCount: { CurrentValue: 5.0 },
              internalRecordCount: { CurrentValue: 5.0 },
            },
            columnDimension: {
              key: 'Centre',
              values: [
                {
                  value: 'C',
                  totalMeasures: {
                    npsScore: { CurrentValue: 0.5 },
                    recordCount: { CurrentValue: 4.0 },
                    internalRecordCount: { CurrentValue: 4.0 },
                  },
                },
              ],
            },
          },
        ],
      },
      rowDimension: {
        key: 'Year',
        values: [
          {
            value: '2022',
            totalMeasures: {
              npsScore: { CurrentValue: -0.6666666666666666 },
              recordCount: { CurrentValue: 6.0 },
              internalRecordCount: { CurrentValue: 6.0 },
            },
            rowDimension: {
              key: 'Month',
              values: [
                {
                  value: '2022-12',
                  totalMeasures: {
                    npsScore: { CurrentValue: -0.6666666666666666 },
                    recordCount: { CurrentValue: 6.0 },
                    internalRecordCount: { CurrentValue: 6.0 },
                  },
                },
              ],
            },
          },
          {
            value: '2023',
            totalMeasures: {
              npsScore: { CurrentValue: 0.8333333333333334 },
              recordCount: { CurrentValue: 6.0 },
              internalRecordCount: { CurrentValue: 6.0 },
            },
            rowDimension: {
              key: 'Month',
              values: [
                {
                  value: '2023-01',
                  totalMeasures: {
                    npsScore: { CurrentValue: 0.75 },
                    recordCount: { CurrentValue: 4.0 },
                    internalRecordCount: { CurrentValue: 4.0 },
                  },
                },
                {
                  value: '2023-02',
                  totalMeasures: {
                    npsScore: { CurrentValue: 1.0 },
                    recordCount: { CurrentValue: 2.0 },
                    internalRecordCount: { CurrentValue: 2.0 },
                  },
                },
              ],
            },
          },
        ],
      },
    },
    dimensionMetadata: {
      Year: { friendlyName: 'Year' },
      State: { friendlyName: 'State' },
      Centre: { friendlyName: 'Centre' },
      Month: { friendlyName: 'Month' },
    },
    measureMetadata: {
      npsScore: { friendlyName: 'NPS Score', format: '0.00' },
      recordCount: { friendlyName: 'Record Count', format: '0' },
      internalRecordCount: { friendlyName: 'Internal Record Count', format: '0' },
      salesAmount: { friendlyName: 'Sales Amount', format: '$0,0' },
      revenueAmount: { friendlyName: 'Revenue Amount', format: '$0,0' },
    },
    highlights: [
      {
        type: 'markPoint',
        semanticValue: 'positive',
        annotation: 'Highest performing metric',
        target: { measureKey: 'salesAmount' },
      },
      {
        type: 'markLine',
        semanticValue: 'warning',
        annotation: 'Revenue threshold',
        target: { measureKey: 'revenueAmount' },
      },
    ],
  },
};

// This matches the actual API response type, not the demo frontend type
export const mockCuratedReportsDataSource: CuratedReportsDataSource = {
  reportNameTemplate: 'Family Experience Program - {Month} {Year}',
  reportType: 'Monthly',
  slides: [
    {
      id: 'cover',
      section: 'Cover',
      subSection: 'CoverPage',
      template: 'SlideTemplateCoverPage',
      visuals: [
        {
          visualKey: 'cover-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'Family Experience Program',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'cover-subtitle',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'subtitle',
          textTemplate: '2025 {{Month}} Report',
          textTemplateValues: {
            Month: 'September',
            Year: '2025',
          },
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'main-chart',
      section: 'NPS Overview',
      subSection: 'NPSPerformance',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'main-chart-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'main-chart-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'nps-chart-1',
          visualType: 'CrNpsStackedBarWithLineChart',
          smartDataModuleId: 'SmartData_CX',
          layoutSlot: 'chart',
          measureKeys: ['npsScore', 'recordCount', 'detractors', 'neutrals', 'promoters'],
          rowDimensions: ['Month'],
          columnDimensions: ['State'],
        },
      ],
    },
    {
      id: 'two-content-chart',
      section: 'NPS Overview',
      subSection: 'Two Content Demo',
      template: 'SlideTemplateText2ChartsHorizontal',
      visuals: [
        {
          visualKey: 'two-content-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'two-content-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'left-nps-chart',
          visualType: 'CrNpsStackedBarWithLineChart',
          smartDataModuleId: 'SmartData_CX',
          layoutSlot: 'leftchart',
          measureKeys: ['npsScore', 'recordCount', 'detractors', 'neutrals', 'promoters'],
          rowDimensions: ['Month'],
          columnDimensions: ['State'],
        },
        {
          visualKey: 'right-nps-chart',
          visualType: 'CrNpsStackedBarWithLineChart',
          smartDataModuleId: 'SmartData_CX',
          layoutSlot: 'rightchart',
          measureKeys: ['npsScore', 'recordCount', 'detractors', 'neutrals', 'promoters'],
          rowDimensions: ['Month'],
          columnDimensions: ['State'],
        },
      ],
    },
    {
      id: 'two-content-chart2',
      section: 'NPS Overview',
      subSection: 'Two Content Demo',
      template: 'SlideTemplateText2ChartsHorizontal',
      visuals: [
        {
          visualKey: 'two-content-chart2-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'two-content-chart2-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'clustered-column-demo',
      section: 'Sales Performance',
      subSection: 'Regional Quarterly Sales',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'clustered-column-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'clustered-column-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'clustered-column-chart-1',
          visualType: 'CrClusteredColumnChart',
          smartDataModuleId: 'SmartData_Sales',
          layoutSlot: 'chart',
          measureKeys: ['salesAmount'],
          rowDimensions: ['Region'],
          columnDimensions: ['Quarter'],
        },
      ],
    },
    {
      id: 'units-performance-demo',
      section: 'Unit Performance',
      subSection: 'Top & Bottom Stores',
      template: 'SlideTemplateText2ChartsVertical',
      visuals: [
        {
          visualKey: 'units-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'Store Performance Analysis',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'units-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          textTemplate: 'Analysis of top and bottom performing stores based on sales metrics.',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'top-units-chart',
          visualType: 'CrTopUnitsChart',
          smartDataModuleId: 'SmartData_Sales',
          layoutSlot: 'topchart',
          measureKeys: ['salesAmount', 'recordCount'],
          rowDimensions: ['Store'],
          columnDimensions: [],
        },
        {
          visualKey: 'bottom-units-chart',
          visualType: 'CrBottomUnitsChart',
          smartDataModuleId: 'SmartData_Sales',
          layoutSlot: 'bottomchart',
          measureKeys: ['salesAmount', 'recordCount'],
          rowDimensions: ['Store'],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'nps-gauge-demo',
      section: 'NPS Overview',
      subSection: 'NPS Gauge',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'nps-gauge-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'NPS Score Summary',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'nps-gauge-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          textTemplate: 'This gauge shows the current NPS score with comparison to the previous period.',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'nps-gauge-chart',
          visualType: 'CrNpsGaugeChart',
          smartDataModuleId: 'SmartData_NPS',
          layoutSlot: 'chart',
          measureKeys: ['npsScore'],
          rowDimensions: [],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'metric-movement-table-demo',
      section: 'NPS Overview',
      subSection: 'Metric Movement Table',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'metric-movement-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'NPS Movement by State',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'metric-movement-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          textTemplate:
            'This table shows NPS movement across different states, sorted by delta from highest to lowest.',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'metric-movement-table',
          visualType: 'CrMetricMovementTable',
          smartDataModuleId: 'SmartData_CX',
          layoutSlot: 'chart',
          measureKeys: ['npsScore', 'recordCount'],
          rowDimensions: ['State'],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'metric-movement-table-demo1',
      section: 'NPS Overview',
      subSection: 'Metric Movement Table',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'metric-movement-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'NPS Movement by State',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'metric-movement-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          textTemplate:
            'This table shows NPS movement across different states, sorted by delta from highest to lowest.',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'metric-tile-table',
          visualType: 'CrMetricTileTable',
          smartDataModuleId: 'SmartData_CX',
          layoutSlot: 'chart',
          measureKeys: ['npsScore', 'recordCount'],
          rowDimensions: ['Quarter'],
          columnDimensions: [],
        },
      ],
    },
    {
      id: 'driver-priority-quadrant-demo',
      section: 'Driver Analysis',
      subSection: 'Driver Priority Quadrant',
      template: 'SlideTemplateTextChartVertical',
      visuals: [
        {
          visualKey: 'driver-priority-title',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'title',
          textTemplate: 'Driver Priority Analysis',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'driver-priority-text',
          visualType: 'CrTextTemplate',
          smartDataModuleId: 'TextData',
          layoutSlot: 'text',
          textTemplate:
            'This quadrant chart compares Driver Importance against Driver Score to identify priorities for action. Critical Success Factors require maintenance, Critical Deficiencies need immediate attention, Lower Priority items can be monitored, and Potential Overinvestment should be evaluated for resource reallocation.',
          measureKeys: [],
          rowDimensions: [],
          columnDimensions: [],
        },
        {
          visualKey: 'driver-priority-quadrant-chart',
          visualType: 'CfgCrMetricDriverPriorityQuadrantChart',
          smartDataModuleId: 'SmartData_Drivers',
          layoutSlot: 'chart',
          measureKeys: ['Msr_DriverImportance', 'Msr_DriverScore', 'RecordCount'],
          rowDimensions: ['DriverName'],
          columnDimensions: [],
        },
      ],
    },
  ],
};

export const mockCuratedReports: CuratedReport[] = [
  {
    year: 2025,
    quarter: 3,
    month: 9,
    isPublished: true,
    reportId: '1',
    reportName: 'BBUK monthly - September 2025',
    reportType: 'Monthly',
    filename: 'report1.pdf',
  },
  {
    year: 2025,
    quarter: 3,
    month: 8,
    isPublished: true,
    reportId: '2',
    reportName: 'BBUK monthly - August 2025',
    reportType: 'Monthly',
    filename: 'report2.pdf',
  },
  {
    year: 2025,
    quarter: 3,
    month: 7,
    isPublished: false,
    reportName: 'BBUK monthly - July 2025',
    reportType: 'Monthly',
  },
  {
    year: 2025,
    quarter: 2,
    month: 6,
    isPublished: true,
    reportId: '4',
    reportName: 'BBUK monthly - June 2025',
    reportType: 'Monthly',
    filename: 'report4.pdf',
  },
  {
    year: 2025,
    quarter: 2,
    month: 5,
    isPublished: true,
    reportId: '5',
    reportName: 'BBUK monthly - May 2025',
    reportType: 'Monthly',
    filename: 'report5.pdf',
  },
];

export const STANDARD_HTTP_MOCKS = [
  http.post<PathParams, DefaultBodyType, any>('https://stagingauth.resonateservices.com/token', async () => {
    return HttpResponse.json({
      content: {
        access_token: 'AReallyLongCode',
        token_type: 'bearer',
        expires_in: 3599,
        refresh_token: 'AShorterCode',
        clientId: '1',
        userName: 'testuser',
        roles: 'GlobalAdmin',
        claims: '',
        firstName: 'Test',
        lastName: 'User',
        createdOn: '2021-02-03',
        changePasswordOnNextLogin: false,
        email: 'testuer@resonatesolutions.com.au',
        id: 'AGuid',
        company: '',
        '.issued': 'Fri, 12 Apr 2024 05:33:33 GMT',
        '.expires': 'Fri, 12 Apr 2024 06:33:33 GMT',
      },
      isSuccess: true,
      statusCode: 200,
    });
  }),
  http.get<PathParams, DefaultBodyType, any>('/version.json', async () => {
    return HttpResponse.json({
      currentVersion: '0.0.0.0',
    });
  }),
  http.post<PathParams, DefaultBodyType, any>(
    'https://unit-test.resonate.cx/api/curatedreports/data/:moduleId/:smartDataModuleId',
    async ({ params }) => {
      // Return text template data for TextData module
      if (params.smartDataModuleId === 'TextData') {
        return HttpResponse.json({
          count: 0,
          data: {},
        });
      }
      // Return chart data for other modules
      return HttpResponse.json(mockCuratedReportSlideData);
    }
  ),
  http.post<PathParams, DefaultBodyType, any>(
    'https://unit-test.resonate.cx/api/curatedreports/data/:moduleId/:slideId/:visualKey',
    async ({ params }) => {
      const visualKey = params.visualKey as string;

      // Mock TextTemplate data based on visual key
      if (visualKey.includes('title')) {
        if (visualKey === 'cover-title') {
          return HttpResponse.json({
            renderedText: 'Family Experience Program',
          });
        } else if (visualKey === 'main-chart-title') {
          return HttpResponse.json({
            renderedText: 'NPS Overview',
          });
        } else if (visualKey === 'two-content-title') {
          return HttpResponse.json({
            renderedText: 'Two Content Chart Demo',
          });
        } else if (visualKey === 'two-content-chart2-title') {
          return HttpResponse.json({
            renderedText: 'Two Content Chart Demo',
          });
        } else if (visualKey === 'clustered-column-title') {
          return HttpResponse.json({
            renderedText: 'Regional Sales Performance',
          });
        } else if (visualKey === 'units-title') {
          return HttpResponse.json({
            renderedText: 'Store Performance Analysis',
          });
        } else if (visualKey === 'vertical-layout-title') {
          return HttpResponse.json({
            renderedText: 'Vertical Charts Layout',
          });
        } else if (visualKey === 'nps-gauge-title') {
          return HttpResponse.json({
            renderedText: 'NPS Score Summary',
          });
        }
      } else if (visualKey.includes('subtitle')) {
        return HttpResponse.json({
          renderedText: '2025 September Report',
        });
      } else if (visualKey.includes('text')) {
        if (visualKey === 'main-chart-text') {
          return HttpResponse.json({
            renderedText: 'This chart shows the Net Promoter Score trends over time.',
          });
        } else if (visualKey === 'two-content-text' || visualKey === 'two-content-chart2-text') {
          return HttpResponse.json({
            renderedText: 'This demonstrates the new two-content layout with the same chart on both sides.',
          });
        } else if (visualKey === 'clustered-column-text') {
          return HttpResponse.json({
            renderedText:
              'This chart shows quarterly sales performance across different regions using a clustered column chart.',
          });
        } else if (visualKey === 'units-text') {
          return HttpResponse.json({
            renderedText: 'Analysis of top and bottom performing stores based on sales metrics.',
          });
        } else if (visualKey === 'vertical-layout-text') {
          return HttpResponse.json({
            renderedText:
              'This slide demonstrates the new layout with two charts stacked vertically on the left and a text description on the right. This layout is useful for comparing two related metrics side-by-side with their explanation.',
          });
        } else if (visualKey === 'nps-gauge-text') {
          return HttpResponse.json({
            renderedText: 'This gauge shows the current NPS score with comparison to the previous period.',
          });
        }
      }

      // Handle NPS Gauge Chart visual
      if (visualKey === 'nps-gauge-chart') {
        return HttpResponse.json({
          visualType: 'CrNpsGaugeChart',
          data: {
            rollupData: {
              value: '',
              totalMeasures: {
                Msr_Svy_Data_NPS_IndexAverage: {
                  CurrentValue: 0.67,
                  PreviousValue: 0.66,
                  Delta: 0.01,
                },
                RecordCount: {
                  CurrentValue: 471.0,
                  PreviousValue: 390.0,
                  Delta: 81.0,
                },
                InternalRecordCount: {
                  CurrentValue: 471.0,
                  PreviousValue: 390.0,
                  Delta: 81.0,
                },
              },
            },
            dimensionMetadata: {},
            measureMetadata: {
              Msr_Svy_Data_NPS_IndexAverage: {
                friendlyName: 'NPS',
                format: '0.00nps',
              },
              RecordCount: {
                friendlyName: 'Responses',
                format: '0',
              },
            },
          },
          count: 0,
        });
      }

      // Handle Delta Bar Chart visual with highlights
      if (visualKey === 'delta-bar-chart-visual') {
        return HttpResponse.json({
          visualType: 'CrDeltaBarChart',
          data: {
            rollupData: {
              value: '',
              totalMeasures: {
                salesAmount: { CurrentValue: 150000 },
                revenueAmount: { CurrentValue: 120000 },
              },
            },
            dimensionMetadata: {},
            measureMetadata: {
              salesAmount: { friendlyName: 'Sales Amount', format: '$0,0' },
              revenueAmount: { friendlyName: 'Revenue Amount', format: '$0,0' },
            },
            highlights: [
              {
                type: 'markPoint',
                semanticValue: 'positive',
                annotation: 'Highest performing metric',
                target: { measureKey: 'salesAmount' },
              },
              {
                type: 'markLine',
                semanticValue: 'warning',
                annotation: 'Revenue threshold',
                target: { measureKey: 'revenueAmount' },
              },
            ],
          },
          count: 1,
        });
      }

      // For non-text visuals, return chart data
      return HttpResponse.json(mockCuratedReportSlideData);
    }
  ),
  http.get<PathParams, DefaultBodyType, CuratedReportsDataSource>(
    'https://unit-test.resonate.cx/api/curatedreports/config/:moduleId',
    async () => HttpResponse.json(mockCuratedReportsDataSource)
  ),
  http.get<PathParams, DefaultBodyType, CuratedReport[]>(
    'https://unit-test.resonate.cx/api/curatedreports/reports/:moduleId',
    async () => HttpResponse.json(mockCuratedReports)
  ),
];

export const cleanHtml = (removeList: string[], html: string): string => {
  const sanitizedHtml = html
    .replace(/data-v-[a-z0-9]*/g, '')
    .replace(RegExp('(?:' + removeList.join('|') + ')=".*?"', 'g'), '')
    .replace(/<!--v-if-->(\n)?/g, '')
    .replace(/=""/g, '');

  return sanitizedHtml;
};
